import { env } from './env.js';

/**
 * The admin API client.
 *
 * Two things separate it from the storefront's: every state-changing request
 * carries the CSRF token from the readable cookie, and a session that cannot be
 * refreshed signs the operator out rather than degrading to a guest — there is
 * no such thing as a guest here.
 */

export class ApiError extends Error {
  constructor({ status, code, message, fields, retryAfterSeconds }) {
    super(message ?? 'Something went wrong.');
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  get isValidation() {
    return this.code === 'VALIDATION_ERROR' && Boolean(this.fields);
  }

  get isForbidden() {
    return this.status === 403;
  }
}

const CSRF_COOKIE = 'ge_csrf';
const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

/** The CSRF cookie is readable by design — that is the double-submit pattern. */
const csrfToken = () => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
};

/**
 * The three routes a 401 must not be recovered from.
 *
 * Everything else — including `/admin/auth/me` — gets a refresh attempt. That
 * distinction is the whole point of this list rather than a prefix test on
 * `/admin/auth/`: the session read lives under that prefix and is the first call
 * the panel makes on load and on every window focus, so excluding the prefix
 * meant the one request most likely to meet an expired access token was also the
 * one request forbidden from repairing it. Fifteen minutes away from the tab and
 * the operator came back to the sign-in screen with a thirty-day refresh token
 * sitting unused in their cookie jar.
 *
 * The three that stay out:
 *   login    — a 401 means the password is wrong; refreshing is not the answer
 *   refresh  — refreshing a failed refresh is a loop
 *   logout   — there is nothing left to recover
 */
const NO_REFRESH = new Set(['/admin/auth/login', '/admin/auth/refresh', '/admin/auth/logout']);

/**
 * Single-flight refresh, for the same reason as the storefront: several grids on
 * one screen can 401 in the same tick, and refresh tokens rotate. Without the
 * lock the later calls would replay an already-rotated token and trip the reuse
 * detection, logging the operator out mid-edit.
 */
let refreshInFlight = null;

const refreshSession = async () => {
  refreshInFlight =
    refreshInFlight ??
    fetch(`${env.apiUrl}/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrfToken() },
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });

  return refreshInFlight;
};

const listeners = new Set();

export const onSessionExpired = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const buildUrl = (path, params) => {
  const url = new URL(`${env.apiUrl}${path}`);

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) url.searchParams.append(key, String(entry));
    } else if (value instanceof Date) {
      url.searchParams.set(key, value.toISOString());
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
};

const parse = async (response) => {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError({
      status: response.status,
      code: 'MALFORMED_RESPONSE',
      message: 'The server sent something we could not read.',
    });
  }
};

const request = async (path, { method = 'GET', body, params, signal, retryOn401 = true, raw = false } = {}) => {
  const isForm = body instanceof FormData;

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      credentials: 'include',
      signal,
      headers: {
        ...(isForm ? {} : { 'Content-Type': 'application/json' }),
        ...(SAFE.has(method) ? {} : { 'X-CSRF-Token': csrfToken() }),
      },
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError({
      status: 0,
      code: 'NETWORK',
      message: 'Could not reach the server. Check your connection.',
    });
  }

  /**
   * A 401 is attempted-then-recovered, never read from the error code.
   *
   * The tempting shortcut is to refresh only when the server says
   * `TOKEN_EXPIRED` and to skip it on `NO_SESSION`, saving a round trip for
   * somebody who was never signed in. It does not work: the access cookie
   * carries a `maxAge`, so the browser deletes it the moment it expires and the
   * request that follows carries no token at all. The server cannot tell that
   * apart from a first visit and answers `NO_SESSION` to both — which is exactly
   * the fifteen-minute case this needs to repair. Verified against the running
   * server before relying on it.
   *
   * So every 401 outside `NO_REFRESH` gets one attempt. The cost is a single
   * failed refresh for a visitor sitting on the sign-in screen; the alternative
   * is signing out operators who are still holding a valid session.
   */
  if (response.status === 401 && retryOn401 && !NO_REFRESH.has(path.split('?')[0])) {
    const recovered = await refreshSession();
    if (recovered) return request(path, { method, body, params, signal, retryOn401: false, raw });
    for (const listener of listeners) listener();
  }

  if (raw) {
    if (!response.ok) {
      const envelope = await parse(response);
      throw new ApiError({
        status: response.status,
        code: envelope?.error?.code ?? 'REQUEST_FAILED',
        message: envelope?.error?.message,
      });
    }
    return response;
  }

  const envelope = await parse(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError({
      status: response.status,
      code: envelope?.error?.code ?? 'REQUEST_FAILED',
      message: envelope?.error?.message,
      fields: envelope?.error?.fields,
      retryAfterSeconds: envelope?.error?.retryAfterSeconds,
    });
  }

  if (envelope && envelope.meta !== undefined && envelope.data !== null && typeof envelope.data === 'object') {
    return Array.isArray(envelope.data)
      ? Object.assign([...envelope.data], { meta: envelope.meta })
      : { ...envelope.data, meta: envelope.meta };
  }

  return envelope?.data ?? null;
};

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  raw: (path, options) => request(path, { ...options, raw: true }),
};

/**
 * Authenticated file responses — invoice PDFs and CSV exports.
 *
 * This is the second of exactly two places a blob URL belongs (blueprint §5).
 * A request started by a plain link or a plain `window.open` carries none of the
 * credential handling `request` does, and the admin API is staff-only, so the
 * response is fetched properly and only then handed to the browser as a blob.
 */

const suggestedName = (response, fallback) =>
  fallback ?? response.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/)?.[1] ?? 'download';

/** Saves the response to disk. Used by the CSV exports. */
export const download = async (path, filename) => {
  const response = await api.raw(path);
  const blob = await response.blob();

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = suggestedName(response, filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * Opens the response in a new tab, in the browser's own viewer. Used by the
 * invoice, which somebody nearly always wants to look at before they want a file
 * — and the viewer still has print and save in it, so nothing is taken away.
 *
 * Two details that are not optional:
 *
 * The tab is opened synchronously, before the fetch, and pointed at the blob
 * afterwards. Opening it after the `await` puts it outside the click that caused
 * it and every popup blocker in use will stop it.
 *
 * The object URL is revoked on a timer rather than immediately. The new tab is
 * still reading from it — revoking on the next line, as a download safely can,
 * leaves the viewer showing nothing at all.
 */
export const openInNewTab = async (path, filename) => {
  const tab = window.open('', '_blank');

  try {
    const response = await api.raw(path);
    const blob = await response.blob();
    const url = URL.createObjectURL(new File([blob], suggestedName(response, filename), { type: blob.type }));

    if (tab) {
      tab.location.href = url;
    } else {
      // Blocked despite the synchronous open. Falling back to this tab is better
      // than a button that silently does nothing.
      window.location.href = url;
    }

    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
};

export default api;
